/**
 * Requirements List Component
 *
 * Displays decomposed requirements grouped by category.
 * Each requirement shows 3 status indicators: Research, Config, Verify
 */

import React from 'react';

export interface RequirementListItem {
  id: string;
  area: string;
  section?: string;
  description: string;
  researchStatus: 'pending' | 'researching' | 'complete' | 'failed' | 'not_supported';
  executionStatus: 'pending' | 'in_progress' | 'completed' | 'failed' | 'skipped' | 'manual';
  verificationStatus: 'not_tested' | 'testing' | 'passed' | 'failed' | 'blocked' | 'manual_needed';
  estimatedComplexity?: number;
}

interface RequirementsListProps {
  requirements: RequirementListItem[];
  selectedId?: string;
  onSelect: (id: string) => void;
  expandedAreas?: string[];
  onToggleArea?: (area: string) => void;
  /** IDs of requirements currently being processed */
  processingIds?: Set<string>;
}

// Status icon components
function StatusIcon({ status, type }: { status: string; type: 'research' | 'config' | 'verify' }) {
  const getIcon = () => {
    switch (status) {
      case 'complete':
      case 'completed':
      case 'passed':
        return (
          <svg className="w-3.5 h-3.5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        );
      case 'failed':
      case 'not_supported':
        return (
          <svg className="w-3.5 h-3.5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        );
      case 'researching':
      case 'in_progress':
      case 'testing':
        return (
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <div className="w-2 h-2 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          </div>
        );
      case 'skipped':
        return (
          <svg className="w-3.5 h-3.5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
            <path fillRule="evenodd" d="M4.293 15.707a1 1 0 010-1.414L8.586 10 4.293 5.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        );
      case 'manual':
      case 'manual_needed':
        return (
          <svg className="w-3.5 h-3.5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
      case 'blocked':
        return (
          <svg className="w-3.5 h-3.5 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <div className="w-3.5 h-3.5 rounded-full border border-gray-600" />
        );
    }
  };

  return (
    <div className="flex items-center justify-center" title={`${type}: ${status}`}>
      {getIcon()}
    </div>
  );
}

// Group requirements by area
function groupByArea(requirements: RequirementListItem[]): Map<string, RequirementListItem[]> {
  const groups = new Map<string, RequirementListItem[]>();
  for (const req of requirements) {
    const existing = groups.get(req.area) ?? [];
    existing.push(req);
    groups.set(req.area, existing);
  }
  return groups;
}

// Calculate area progress
function calculateAreaProgress(requirements: RequirementListItem[]): number {
  if (requirements.length === 0) return 0;
  const completed = requirements.filter(
    r => r.verificationStatus === 'passed' || r.executionStatus === 'completed'
  ).length;
  return Math.round((completed / requirements.length) * 100);
}

export function RequirementsList({
  requirements,
  selectedId,
  onSelect,
  expandedAreas = [],
  onToggleArea,
  processingIds = new Set(),
}: RequirementsListProps) {
  const groupedRequirements = groupByArea(requirements);
  const [localExpanded, setLocalExpanded] = React.useState<string[]>([]);

  const expanded = expandedAreas.length > 0 ? expandedAreas : localExpanded;
  const toggleArea = onToggleArea ?? ((area: string) => {
    setLocalExpanded(prev =>
      prev.includes(area) ? prev.filter(a => a !== area) : [...prev, area]
    );
  });

  return (
    <div className="space-y-2">
      {Array.from(groupedRequirements.entries()).map(([area, areaRequirements]) => {
        const isExpanded = expanded.includes(area);
        const progress = calculateAreaProgress(areaRequirements);

        return (
          <div key={area} className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
            {/* Area header */}
            <button
              onClick={() => toggleArea(area)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-750 transition-colors"
            >
              <div className="flex items-center gap-3">
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium text-gray-100">
                  {area} ({areaRequirements.length})
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-20 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className={`text-xs font-medium ${
                  progress === 100 ? 'text-green-400' :
                  progress > 50 ? 'text-yellow-400' : 'text-gray-500'
                }`}>
                  {progress}%
                </span>
              </div>
            </button>

            {/* Requirements list */}
            {isExpanded && (
              <div className="border-t border-gray-700">
                {areaRequirements.map(req => {
                  const isProcessing = processingIds.has(req.id);
                  return (
                  <button
                    key={req.id}
                    onClick={() => onSelect(req.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-750 transition-colors ${
                      selectedId === req.id ? 'bg-gray-700' : ''
                    } ${isProcessing ? 'bg-blue-900/30 border-l-2 border-blue-400' : ''}`}
                  >
                    {/* Status indicators */}
                    <div className="flex items-center gap-1">
                      <StatusIcon status={req.researchStatus} type="research" />
                      <StatusIcon status={req.executionStatus} type="config" />
                      <StatusIcon status={req.verificationStatus} type="verify" />
                    </div>

                    {/* Description */}
                    <span className="flex-1 text-sm text-gray-300 truncate">
                      {req.description}
                    </span>

                    {/* Complexity badge */}
                    {req.estimatedComplexity && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        req.estimatedComplexity >= 4 ? 'bg-red-900/50 text-red-400' :
                        req.estimatedComplexity >= 3 ? 'bg-yellow-900/50 text-yellow-400' :
                        'bg-gray-700 text-gray-400'
                      }`}>
                        C{req.estimatedComplexity}
                      </span>
                    )}
                  </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {requirements.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No requirements extracted yet
        </div>
      )}
    </div>
  );
}
